import React from 'react';
import ResponsiveECharts from './ResponsiveECharts';
import { baseOption, axeCategorie, axeValeur, TAILLE_TEXTE, COULEUR_TEXTE } from './echartsTheme';
import { ALEA_INFO } from '../constants';

const arrondi1 = (v) => Math.round(v * 10) / 10;

/**
 * Pertes par culture, empilées par aléa (Impacts globaux, image 490).
 *
 * Les couleurs et libellés viennent d'ALEA_INFO, comme le camembert des aléas :
 * un même aléa garde la même couleur d'un graphique à l'autre.
 *
 * Avec `annees`, chaque culture porte une barre empilée par horizon (côte à
 * côte), l'horizon étant écrit au-dessus de sa barre. Sans `annees`, `data`
 * donne une seule barre par culture (ancien usage).
 *
 * @param annees [{ cle, label, data: [{ culture, Gel, CDF, CDC, EE, SHRU }] }]
 * @param data   [{ culture, Gel, CDF, CDC, EE, SHRU }]
 */
export default function AleasParCultureChart({ annees, data = [] }) {
  const groupes = (annees ?? [{ cle: 'unique', label: null, data }]).filter(
    (g) => g.data?.length > 0
  );

  if (groupes.length === 0) {
    return <p className="tdb-chargement">Aucune donnée par culture.</p>;
  }

  const codes = Object.keys(ALEA_INFO);

  // Cultures : union de tous les horizons, dans l'ordre de première apparition.
  const cultures = [];
  groupes.forEach((g) =>
    g.data.forEach((d) => {
      if (!cultures.includes(d.culture)) cultures.push(d.culture);
    })
  );

  const plusieurs = groupes.length > 1;

  // Une pile par horizon (stack = clé de l'horizon), une série par aléa dans
  // chaque pile. Les séries d'un même aléa partagent le même nom : la légende
  // n'affiche donc que les 5 aléas, et un clic masque l'aléa sur toutes les
  // piles à la fois.
  const series = groupes.flatMap((g) => {
    const parCulture = new Map(g.data.map((d) => [d.culture, d]));
    return codes.map((code, i) => ({
      id: `${g.cle}|${code}`,
      name: ALEA_INFO[code].label,
      type: 'bar',
      stack: `aleas-${g.cle}`,
      barMaxWidth: plusieurs ? 36 : 64,
      itemStyle: { color: ALEA_INFO[code].color },
      // Libellé de l'horizon posé au-dessus de la pile (sur la dernière série).
      label:
        plusieurs && i === codes.length - 1
          ? {
              show: true,
              position: 'top',
              fontSize: 12,
              color: COULEUR_TEXTE,
              formatter: () => g.label,
            }
          : { show: false },
      data: cultures.map((c) => parCulture.get(c)?.[code] ?? 0),
    }));
  });

  const labelParCle = new Map(groupes.map((g) => [String(g.cle), g.label]));

  const option = {
    ...baseOption(),
    grid: { left: 12, right: 24, bottom: 48, top: 64, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      textStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      formatter: (params) => {
        if (params.length === 0) return '';
        let html = `<b>${params[0].axisValueLabel}</b>`;
        // Regroupement des lignes par horizon, avec le total de la pile.
        const parHorizon = new Map();
        params.forEach((p) => {
          const cle = String(p.seriesId).split('|')[0];
          if (!parHorizon.has(cle)) parHorizon.set(cle, []);
          parHorizon.get(cle).push(p);
        });
        parHorizon.forEach((lignes, cle) => {
          const total = lignes.reduce((s, p) => s + (Number(p.value) || 0), 0);
          const titre = labelParCle.get(cle);
          if (titre) html += `<br/><br/><b>${titre}</b> : ${arrondi1(total)} %`;
          lignes.forEach((p) => {
            html += `<br/>${p.marker} ${p.seriesName} : ${p.value} %`;
          });
        });
        return html;
      },
    },
    xAxis: axeCategorie({ data: cultures }),
    yAxis: axeValeur({ nom: 'Pertes (%)', formatteur: (v) => `${v} %` }),
    series,
  };

  return <ResponsiveECharts option={option} style={{ height: plusieurs ? 420 : 380 }} />;
}
